import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SignalActionsContext } from "@shared/features/signals/common/SignalActions.context";
import BulkCancelSignalsModal from "features/signals/CancelSignal/BulkCancelSignalsModal";
import { openMessage } from "utils/misc";
import { useRemoveDeselectedItems } from "@shared/utils/hooks/useRemoveDeselectedItems.ts";
import { SignalsPageContext } from "@shared/features/signals/common/SignalsPage.context.ts";
import { useIsPersonalGroup } from "@shared/features/signals/common/hooks.ts";

const SignalActionsProvider = ({ children }: PropsWithChildren) => {
  const { isOpen, isAdminSignals, signalsData, subGroupId } =
    useContext(SignalsPageContext)!;
  const [selectedSignalIDs, setSelectedSignalIDs] = useState<number[]>([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkCloseSignalsModalOpen, setBulkCloseSignalsModalOpen] =
    useState(false);

  const signalIDs = useMemo(() => {
    return signalsData?.map((s) => s.signal_id) || [];
  }, []);

  useEffect(() => {
    if (selectedSignalIDs.length && !isBulkMode) setIsBulkMode(true);
  }, [selectedSignalIDs]);

  const exitBulkMode = () => {
    setSelectedSignalIDs([]);
    setIsBulkMode(false);
  };

  useEffect(() => {
    exitBulkMode();
  }, [isOpen, subGroupId]);

  const actionButtonsData = [
    {
      text: "Close Signals",
      onClick: () => setBulkCloseSignalsModalOpen(true),
    },
  ];

  const selectAll = (ids: number[]) => setSelectedSignalIDs(ids);

  const isPersonalGroupSignals = useIsPersonalGroup(
    signalsData?.[0]?.sub_group_id,
    isAdminSignals || !isOpen
  );
  const showEditSignalActions =
    isOpen && (isAdminSignals || isPersonalGroupSignals);

  const signalIDsSet = useMemo(() => new Set(signalIDs), [signalIDs]);

  useRemoveDeselectedItems(
    selectedSignalIDs,
    signalIDsSet,
    setSelectedSignalIDs,
    "signals",
    openMessage
  );

  const closeBulkCloseSignalsModal = () => setBulkCloseSignalsModalOpen(false);

  const onFinishBulkCloseSignals = () => setSelectedSignalIDs([]);

  const contextValue = useMemo(
    () => ({
      selectedSignalIDs,
      setSelectedSignalIDs,
      isBulkMode,
      setIsBulkMode,
      bulkCloseSignalsModalOpen,
      setBulkCloseSignalsModalOpen,
      actionButtonsData,
      exitBulkMode,
      selectAll,
      signalIDs,
      showEditSignalActions,
    }),
    [
      selectedSignalIDs,
      isBulkMode,
      bulkCloseSignalsModalOpen,
      signalIDs,
      showEditSignalActions,
    ]
  );

  return (
    <SignalActionsContext.Provider value={contextValue}>
      {children}
      <BulkCancelSignalsModal
        open={bulkCloseSignalsModalOpen}
        signalIDs={selectedSignalIDs}
        closeModal={closeBulkCloseSignalsModal}
        onFinish={onFinishBulkCloseSignals}
      />
    </SignalActionsContext.Provider>
  );
};

export default SignalActionsProvider;
